import { useAddNativeClasses } from '@carvana/merch-native/hooks';
import { useContext } from 'react';
import useDisplayTradeAppliedToast from '~/components/common/TradeAppliedToastContent/useDisplayTradeAppliedToast';
import { heliosSizes } from '~/constants/common/resizeBreakpoints';
import { useNativeDeviceType } from '~/context/common/DeviceContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import ModalContext from '~/context/srp/ModalContext';
import useToggleAccountModal from '~/hooks/common/accountModal/useToggleAccountModal';
import useGetFavorites from '~/hooks/common/favorites/useGetFavorites';
import useGeolocation from '~/hooks/common/geolocation/useGeolocation';
import useGetSavedSearches from '~/hooks/common/services/useGetSavedSearches';
import useGetCommonTrades from '~/hooks/common/services/useGetTrades';
import useSyncGeolocation from '~/hooks/common/sync/useSyncGeolocation';
import useSynchronizeAuth from '~/hooks/common/sync/useSynchronizeAuth';
import useToggleAppliedTradeCookie from '~/hooks/common/sync/useToggleAppliedTradeCookie';
import useDataDogRUM from '~/hooks/common/telemetry/useDataDogRUM';
import useSetTradeIncentiveDDContext from '~/hooks/common/telemetry/useSetTradeIncentiveDDContext';
import useTrackBrowserCookieIdMismatch from '~/hooks/common/telemetry/useTrackBrowserCookieIdMismatch';
import { useTrackClickEvents } from '~/hooks/common/telemetry/useTrackClickEvents';
import useTrackUserGeolocations from '~/hooks/common/telemetry/useTrackUserGeolocations';
import useResizeListener from '~/hooks/common/ui/useResizeListener';
import useSetScrollbarWidthProp from '~/hooks/common/ui/useSetScrollbarWidthProp';
import useSebastianLoaded from '~/hooks/common/useSebastianLoaded';
import { useTrackResults } from '~/hooks/srp/events';
import { useGetLowInventoryAvailability } from '~/hooks/srp/services/useGetAvailability';
import useGetAvailability from '~/hooks/srp/services/useGetAvailability';
import useGetInventory, {
  useGetFacetData,
  useGetSuggestedFilters
} from '~/hooks/srp/services/useGetInventory';
import useGetPricing from '~/hooks/srp/services/useGetPricing';
import useAddMetaAttributes from '~/hooks/srp/useAddMetaAttributes';
import useAlertHighNetTradeIssues from '~/hooks/srp/useAlertHighNetTradeIssues';
import useEnsureFinanceFiltersAreInRange from '~/hooks/srp/useEnsureFinanceFiltersAreInRange';
import useEnsureValidSortBy from '~/hooks/srp/useEnsureValidSortBy';
import useGetCreditStatus from '~/hooks/srp/useGetCreditStatus';
import useLogHasNewVehicleFilter from '~/hooks/srp/useLogHasNewVehicleFilter';
import useLogMissingConditionalTiles from '~/hooks/srp/useLogMissingConditionalTiles';
import useLogZipCodeChanges from '~/hooks/srp/useLogZipCodeChanges';
import useMerchWindowObject from '~/hooks/srp/useMerchWindowObject';
import useModalScrollRestoration from '~/hooks/srp/useModalScrollRestoration';
import useNavigation from '~/hooks/srp/useNavigation';
import useQualiModal from '~/hooks/srp/useQualiModal';
import useRegenerateSearchSessionId from '~/hooks/srp/useRegenerateSearchSessionId';
import useResetSortOnNewSearch from '~/hooks/srp/useResetSortOnNewSearch';
import useScrollToTop from '~/hooks/srp/useScrollToTop';
import useSearchAnalyzeEventListener from '~/hooks/srp/useSearchAnalyzeEventListener';
import useSendTradeIncentiveEvents from '~/hooks/srp/useSendTradeIncentiveEvents';
import useSetDataDogContext from '~/hooks/srp/useSetDataDogContext';
import useSetPageNumber from '~/hooks/srp/useSetPageNumber';
import useShowFinance from '~/hooks/srp/useShowFinance';
import useShowManageSavedSearchesOnLoad from '~/hooks/srp/useShowManageSavedSearchesOnLoad';
import useSyncUrl from '~/hooks/srp/useSyncUrl';
import useTrackSuggestedFilters from '~/hooks/srp/useTrackSuggestedFilters';
import useTrackTilePerformance from '~/hooks/srp/useTrackTilePerformance';
import useTradeToggled from '~/hooks/srp/useTradeToggled';
import {
  useNativeMessageDispatcher,
  useNativeMessageListener
} from '~/native/srp';

type OwnProps = {
  fetchedFacetDataOnServer: boolean;
  fetchedInventoryOnServer: boolean;
};

export default function SearchData(props: OwnProps) {
  const { isAccountModalOpen, setAccountModalOpen } = useContext(ModalContext);
  const isLoggedIn = useIsLoggedIn();
  const nativeDeviceType = useNativeDeviceType();
  useAddMetaAttributes();
  useDataDogRUM();
  useSynchronizeAuth();
  useSyncUrl();
  useShowManageSavedSearchesOnLoad();
  useGetCreditStatus();
  useGetCommonTrades(isLoggedIn);
  useTradeToggled();
  useDisplayTradeAppliedToast();
  useResizeListener(heliosSizes);
  useSetScrollbarWidthProp();
  useGetFavorites();
  useGetFacetData(props.fetchedFacetDataOnServer);
  useGetInventory(props.fetchedInventoryOnServer);
  useGetSuggestedFilters();
  useTrackSuggestedFilters();
  useGetAvailability();
  useGetPricing();
  useGetSavedSearches();
  useNavigation();
  useScrollToTop();
  useSetPageNumber();
  useShowFinance();
  useToggleAppliedTradeCookie();
  useTrackClickEvents();
  useTrackResults();
  useTrackTilePerformance();
  useSetDataDogContext();
  useGetLowInventoryAvailability();
  useLogZipCodeChanges();
  useNativeMessageDispatcher();
  useNativeMessageListener();
  useAddNativeClasses(nativeDeviceType.isNative, nativeDeviceType.platform);
  useModalScrollRestoration();
  useMerchWindowObject();
  useRegenerateSearchSessionId();
  useResetSortOnNewSearch();
  useEnsureFinanceFiltersAreInRange();
  useEnsureValidSortBy();
  useTrackBrowserCookieIdMismatch();
  useSearchAnalyzeEventListener();
  useSendTradeIncentiveEvents();
  useSetTradeIncentiveDDContext();
  useLogHasNewVehicleFilter();
  useLogMissingConditionalTiles();
  useGeolocation();
  useSyncGeolocation();
  useToggleAccountModal({ isAccountModalOpen, setAccountModalOpen });
  useAlertHighNetTradeIssues();
  useTrackUserGeolocations();
  useQualiModal();
  useSebastianLoaded();

  return null;
}
